import { followBoard, likeBoard, unfollowBoard, unlikeBoard } from "@/services";
import { checkLogin, openBoardModal } from "@/utils/open";
import useReq from "@/utils/useReq";
import { useMemoizedFn } from "ahooks";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

export function useBoardAction(initBoard: IBoardInfo) {
  const router = useRouter();
  const [board, setBoard] = useState(initBoard);
  const [liked, setLiked] = useState(board?.liked || false);
  const { runAsync: runLike } = useReq(likeBoard, {
    manual: true,
  });

  const { runAsync: runUnlike } = useReq(unlikeBoard, {
    manual: true,
  });

  const [following, setFollowing] = useState(board?.following || false);
  const { runAsync: runFollow } = useReq(followBoard, {
    manual: true,
  });
  const { runAsync: runUnfollow } = useReq(unfollowBoard, {
    manual: true,
  });

  const onToggleFollow = useMemoizedFn(async () => {
    const user = await checkLogin();

    if (!user) {
      return;
    }

    if (following) {
      const result = await runUnfollow(board.board_id);
      if (!result.err) {
        setFollowing(false);
        setBoard({
          ...board,
          follow_count: board.follow_count - 1,
          following: false,
        });
      }
    } else {
      const result = await runFollow(board.board_id);
      if (!result.err) {
        setFollowing(true);
        setBoard({
          ...board,
          follow_count: board.follow_count + 1,
          following: true,
        });
      }
    }
  });

  const onToggleLike = useMemoizedFn(async () => {
    const user = await checkLogin();
    if (!user) {
      return;
    }

    let result: any;
    if (liked) {
      result = await runUnlike(board.board_id);
    } else {
      result = await runLike(board.board_id);
    }

    if (!result.err) {
      setLiked((liked) => !liked);
      setBoard({
        ...board,
        liked: !liked,
      });
    }
  });

  const onCategorySelect = useMemoizedFn((category) => {
    setBoard((board) => {
      return {
        ...board,
        category_id: category.value,
        category_name: category.name,
      };
    });
  });

  const onBoardEdit = useMemoizedFn(() => {
    openBoardModal({
      boardId: board.board_id,
      name: "boardEditModal",
      onOk: (record) => {
        setBoard({
          ...board,
          ...record,
        });
      },
      onDelete: () => {
        router.push(`/user/${board?.user?.urlname}`);
      },
    });
  });

  useEffect(() => {
    setLiked(board?.liked || false);
    setFollowing(board?.following || false);
  }, [board]);

  return {
    board,
    setBoard,
    following,
    setFollowing,
    liked,
    setLiked,
    onToggleFollow,
    onToggleLike,
    onCategorySelect,
    onBoardEdit,
  };
}
